import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { Link } from 'react-router-dom';

import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { API_BASE_URL } from '../../config.js';
import DataTable from 'react-data-table-component';
import ReactHtmlParser from 'react-html-parser';

import EntriesSelector from '../Pagination/EntriesSelector';

import {
  statisticsAction,
  statisticsCloseAction
} from '../../actions/settingsAction.js';

const Deptpermission = (props) => {

  const { fixNavbar } = props;

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isApproving, setIsApproving] = useState(false);
  const approvingRef = useRef(false);

  /* ---------------- FETCH DATA ---------------- */
  const loadData = () => {
    // hod level list - rows where the logged in user is the employee's hod
    axios.get(API_BASE_URL + '/dept_permission', {
      headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
    })
      .then(res => {
        setData(res.data || []);
      })
      .catch(err => {
        toast.error(
          err?.response?.data?.message || "Failed to load permission list",
          { position: "bottom-right", theme: "colored" }
        );
      });
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ---------------- SEARCH FILTER ---------------- */
  const filteredItems = data.filter(item =>
    `${item.employee_id ?? ''} ${item.full_name ?? ''} ${item.department ?? ''}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ---------------- BULK APPROVE ---------------- */
  // incharge must have approved first, and dept must still be pending
  const isApprovable = row => row.incharge_id == 2 && row.dept_status_id == 1;

  const approvableRows = filteredItems.filter(isApprovable);
  const isAllSelected =
    approvableRows.length > 0 &&
    approvableRows.every(row => selectedIds.includes(row.id));

  const toggleSelectAll = () => {
    setSelectedIds(isAllSelected ? [] : approvableRows.map(row => row.id));
  };

  const handleCheckboxChange = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleApproveSelected = () => {
    // guard against double / rapid clicks while a request is already in flight
    if (approvingRef.current) return;

    if (selectedIds.length === 0) {
      toast.error("No permission selected for approval", {
        toastId: "no-permission-selected", // prevents the same toast stacking on repeat clicks
        position: "bottom-right",
        theme: "colored",
      });
      return;
    }

    approvingRef.current = true;
    setIsApproving(true);

    axios.post(
      API_BASE_URL + '/dept_approve_permission',
      { permission_id: selectedIds },
      { headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` } }
    )
      .then(res => {
        if (res.data && res.data.status === 200) {
          toast.success(res.data.message || "Selected permissions approved successfully", {
            position: "bottom-right",
            theme: "colored",
          });
          setSelectedIds([]);
          loadData();
        } else {
          toast.error((res.data && res.data.message) || "Failed to approve", {
            toastId: "approve-permission",
            position: "bottom-right",
            theme: "colored",
          });
        }
      })
      .catch(err => {
        toast.error(
          err?.response?.data?.message || "Failed to approve selected permissions",
          { toastId: "approve-permission", position: "bottom-right", theme: "colored" }
        );
      })
      .finally(() => {
        approvingRef.current = false;
        setIsApproving(false);
      });
  };

  /* ---------------- DATATABLE COLUMNS ---------------- */
  const columns = [
    {
      name: (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={toggleSelectAll}
          disabled={approvableRows.length === 0}
        />
      ),
      cell: row =>
        isApprovable(row) ? (
          <input
            type="checkbox"
            checked={selectedIds.includes(row.id)}
            onChange={() => handleCheckboxChange(row.id)}
          />
        ) : null,
      ignoreRowClick: true,
      width: '60px',
    },
    {
      name: 'Action',
      selector: row => (row.incharge_id == 2 && row.dept_status_id == 1 ? 1 : 0),
      sortable: true,
      cell: row =>
        row.incharge_id == 2 && row.dept_status_id == 1 ? (
          <Link to={`/Dept-permission-details?id=${row.id}`}>
            <i className="fe fe-maximize" />
          </Link>
        ) : null,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
    {
      name: 'Employee Id',
      selector: row => row.employee_id,
      sortable: true,
    },
    {
      name: 'Employee',
      selector: row => row.full_name,
      sortable: true,
    },
    {
      name: 'Department',
      selector: row => row.department,
      sortable: true,
    },
    {
      name: 'Permission Date',
      selector: row => row.permission_date,
      sortable: true,
    },
    {
      name: 'Permission Hour',
      selector: row => row.permission_hours,
      sortable: true,
    },
    {
      name: 'In Time',
      selector: row => row.in_time,
      sortable: true,
    },
    {
      name: 'Out Time',
      selector: row => row.out_time,
      sortable: true,
    },
    {
      name: 'Applied On',
      selector: row => row.applied_on,
      sortable: true,
    },
    {
      name: 'Reason',
      selector: row => row.reason,
      wrap: true,
    },
    {
      name: 'Incharge',
      cell: row => ReactHtmlParser(row.incharge),
    },
    {
      name: 'HOD',
      cell: row => ReactHtmlParser(row.dept),
    },
    {
      name: 'HR',
      cell: row => ReactHtmlParser(row.hr_status),
    },
  ];

  return (
    <>
      <div className={`section-body ${fixNavbar ? "marginTop" : ""}`}>
        <ToastContainer />

        <div className="container-fluid">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
                  <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/><path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/><path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
                </svg>
                Manage Permission
              </h3>

              <div style={{ display: 'flex', alignItems: 'center' }}>
                <EntriesSelector
                  setEntriesPerPage={setEntriesPerPage}
                />

                <div style={{ position: "relative", width: "250px", marginLeft: "15px" }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <span
                      onClick={() => setSearchTerm("")}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#999"
                      }}
                    >
                      ✖
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body">
              <DataTable
                columns={columns}
                data={filteredItems}
                striped
                highlightOnHover
                responsive
                persistTableHead
                noDataComponent="No permission records found"
              />

              <button
                className="btn btn-primary mt-3"
                onClick={handleApproveSelected}
                disabled={isApproving}
              >
                {isApproving ? "Approving..." : "Approve Permission"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  fixNavbar: state.settings.isFixNavbar,
  statisticsOpen: state.settings.isStatistics,
  statisticsClose: state.settings.isStatisticsClose,
});

const mapDispatchToProps = (dispatch) => ({
  statisticsAction: (e) => dispatch(statisticsAction(e)),
  statisticsCloseAction: (e) => dispatch(statisticsCloseAction(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Deptpermission);
